import { DateTime } from 'luxon';
/**
 * Age group brackets used for categorizing players
 * Players are placed in the next age bracket "U" (Under)
 * Example: A 14-year-old is in 15U
 */
export const AGE_GROUP_BRACKETS = [8, 10, 12, 14, 15, 16, 17, 18, 19];
export const formatAgeGroup = (ageGroup) => {
    return `${ageGroup}u`;
};
export const renderAgeGroup = (ageGroup, options = { shouldDisplay: true }) => {
    if (ageGroup && options.shouldDisplay) {
        return formatAgeGroup(ageGroup);
    }
    return undefined;
};
/**
 * Calculates the age of a person given their birthdate and an effective date
 * @param birthdate - ISO date string of the person's birthdate
 * @param effectiveDate - ISO date string for the effective date (e.g., program start date)
 * @returns Age in years, or null if birthdate is invalid
 */
export const calculateAge = (birthdate, effectiveDate) => {
    if (!birthdate || !effectiveDate) {
        return null;
    }
    try {
        const birth = DateTime.fromISO(birthdate);
        const effective = DateTime.fromISO(effectiveDate);
        if (!birth.isValid || !effective.isValid) {
            return null;
        }
        // Luxon's diff method with years unit gives us the age
        const age = Math.floor(effective.diff(birth, 'years').years);
        return age;
    }
    catch (_a) {
        return null;
    }
};
/**
 * Calculates the age group for a given age
 * This logic can be easily modified by changing AGE_GROUP_BRACKETS
 *
 * Current logic: Players are placed in the next age bracket "U" (Under)
 * Example: A 14-year-old is in 15U
 *
 * @param age - The player's age in years
 * @returns Age group string (e.g., "15U", "18U") or null if age is invalid
 */
export const calculateAgeGroup = (age) => {
    if (age === null || age < 0) {
        return null;
    }
    // Find the appropriate age group bracket
    for (const bracket of AGE_GROUP_BRACKETS) {
        if (age < bracket) {
            return `${bracket}U`;
        }
    }
    // If older than all brackets, put in the highest bracket
    const highestBracket = AGE_GROUP_BRACKETS.at(-1);
    return `${highestBracket}U`;
};
/**
 * Validates if a player's age is within the allowed range for a program
 * based on their birthdate, effective date, and maximum age setting.
 *
 * @param birthDate - Player's birthdate in 'yyyy-MM-dd' format
 * @param effectiveDate - The effective date for age calculation (from program settings)
 * @param maximumAge - Maximum allowed age from program settings
 * @returns true if player is within age range, false if they exceed the maximum age
 *
 * @example
 * isWithinAgeRange('2010-05-15', '2024-01-01', 13) // returns true if player is 13 or younger
 */
export const isWithinAgeRange = (birthDate, effectiveDate, maximumAge) => {
    // Skip validation if any required value is missing
    if (!birthDate || !effectiveDate || !maximumAge) {
        return true;
    }
    const formattedBirthDate = DateTime.fromFormat(birthDate, 'yyyy-MM-dd');
    const formattedEffectiveDate = DateTime.fromFormat(effectiveDate.replace('T', ' ').split(' ')[0], 'yyyy-MM-dd');
    // Validate that dates are valid
    if (!formattedBirthDate.isValid || !formattedEffectiveDate.isValid) {
        return true; // Skip validation if dates are invalid
    }
    const age = formattedEffectiveDate.diff(formattedBirthDate, 'years').years;
    // Player must be under maximum_age + 1 (e.g., if max is 13, player can be 13 but not 14)
    return age < maximumAge + 1;
};
